export function getStyles(accent: string, accentSecondary: string | null, position: 'right' | 'left'): string {
  const secondary = accentSecondary ?? accent;
  const positionProp = position === 'left' ? 'left' : 'right';
  const oppositeProp = position === 'left' ? 'right' : 'left';

  return `
:host {
  --att-accent: ${accent};
  --att-accent-secondary: ${secondary};
  --att-white: #ffffff;
  --att-text: #1a1f36;
  --att-text-muted: #6b7280;
  --att-border: #e5e7eb;
  --att-bg: #f9fafb;
  --att-bubble-radius: 18px;
  --att-shadow: 0 28px 80px -16px rgba(11,18,32,.32);
  --att-panel-w: 380px;
  --att-panel-h: 600px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--att-text);
  box-sizing: border-box;
}

*, *::before, *::after {
  box-sizing: inherit;
}

/* ===== TRIGGER ===== */
.att-trigger {
  position: fixed;
  bottom: 24px;
  ${positionProp}: 24px;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  outline: none;
  transition: transform 0.18s ease;
  font-family: inherit;
}

.att-trigger:hover {
  transform: scale(1.05);
}

.att-trigger:active {
  transform: scale(0.97);
}

.att-trigger--pill {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--att-accent);
  color: var(--att-white);
  border-radius: 50px;
  padding: 0 20px 0 14px;
  height: 52px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2), 0 0 0 0 var(--att-accent);
  transition: box-shadow 0.2s ease, transform 0.18s ease;
  white-space: nowrap;
}

.att-trigger--pill:hover {
  box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 0 0 4px color-mix(in srgb, var(--att-accent) 20%, transparent);
}

.att-trigger--circle {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--att-accent);
  color: var(--att-white);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2);
  transition: box-shadow 0.2s ease, transform 0.18s ease;
  flex-shrink: 0;
}

.att-trigger--circle:hover {
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
}

.att-trigger--logo {
  background: transparent;
  box-shadow: none;
  width: 60px;
  height: 60px;
  padding: 0;
  filter: drop-shadow(0 4px 12px rgba(0,80,255,0.35));
}

.att-trigger--logo:hover {
  filter: drop-shadow(0 6px 20px rgba(0,80,255,0.5));
}

.att-trigger--logo .att-trigger-icon {
  width: 60px;
  height: 60px;
}

.att-trigger--logo .att-trigger-icon svg {
  width: 60px;
  height: 60px;
}

.att-trigger-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.att-trigger-icon svg {
  width: 22px;
  height: 22px;
}

.att-trigger-label {
  font-size: 15px;
  font-weight: 600;
}

/* Unread badge */
.att-badge {
  position: absolute;
  top: -4px;
  ${oppositeProp}: -4px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
  z-index: 1;
  animation: att-badge-pop 0.2s ease;
}

@keyframes att-badge-pop {
  0% { transform: scale(0); }
  70% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.att-badge--hidden {
  display: none;
}

/* Proactive balloon */
.att-proactive {
  position: fixed;
  bottom: 88px;
  ${positionProp}: 20px;
  background: var(--att-white);
  border-radius: 16px 16px ${position === 'right' ? '4px' : '16px'} ${position === 'left' ? '4px' : '16px'};
  padding: 12px 16px;
  max-width: 260px;
  font-size: 14px;
  color: var(--att-text);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  animation: att-proactive-in 0.3s ease;
  z-index: 2147483001;
  cursor: pointer;
  border: 1px solid var(--att-border);
}

.att-proactive::after {
  content: '';
  position: absolute;
  bottom: -8px;
  ${positionProp}: 28px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid var(--att-white);
}

.att-proactive-close {
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--att-text-muted);
  font-size: 16px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}

.att-proactive-close:hover {
  background: var(--att-bg);
  color: var(--att-text);
}

@keyframes att-proactive-in {
  from { opacity: 0; transform: translateY(8px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ===== PANEL ===== */
.att-panel {
  position: fixed;
  bottom: 96px;
  ${positionProp}: 24px;
  width: var(--att-panel-w);
  height: var(--att-panel-h);
  background: var(--att-white);
  border-radius: 22px;
  box-shadow: var(--att-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483000;
  border: 1px solid rgba(0,0,0,0.08);
  transform-origin: bottom ${positionProp};
  opacity: 0;
  transform: translateY(12px) scale(0.985);
  pointer-events: none;
  transition: opacity 220ms ease-out, transform 220ms ease-out;
  will-change: opacity, transform;
}

.att-panel--open {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: all;
}

/* ===== HEADER ===== */
.att-header {
  background: linear-gradient(135deg, var(--att-accent) 0%, var(--att-accent-secondary) 100%);
  padding: 20px 16px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  position: relative;
}

.att-header-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.att-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--att-white);
  overflow: hidden;
  flex-shrink: 0;
}

.att-avatar + .att-avatar {
  margin-left: -10px;
}

.att-avatar--agent {
  width: 28px;
  height: 28px;
  font-size: 11px;
  border: 2px solid var(--att-white);
}

.att-header-info {
  flex: 1;
  min-width: 0;
}

.att-header-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--att-white);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.att-header-subtitle {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.att-header-online {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  margin-right: 5px;
  vertical-align: middle;
  box-shadow: 0 0 0 2px rgba(34,197,94,0.3);
}

.att-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.att-header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255,255,255,0.15);
  color: var(--att-white);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.15s ease;
  font-family: inherit;
}

.att-header-btn:hover {
  background: rgba(255,255,255,0.25);
}

/* ===== MESSAGES ===== */
.att-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--att-bg);
  scroll-behavior: smooth;
}

.att-messages::-webkit-scrollbar {
  width: 4px;
}

.att-messages::-webkit-scrollbar-track {
  background: transparent;
}

.att-messages::-webkit-scrollbar-thumb {
  background: var(--att-border);
  border-radius: 2px;
}

.att-messages::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}

/* Welcome message */
.att-welcome {
  text-align: center;
  color: var(--att-text-muted);
  font-size: 13px;
  padding: 8px 12px;
  margin: 4px 0 8px;
}

/* Message row */
.att-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  animation: att-msg-in 0.2s ease;
}

@keyframes att-msg-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.att-msg-row--visitor {
  flex-direction: row-reverse;
}

.att-msg-row--agent {
  flex-direction: row;
}

.att-msg-avatar-wrap {
  flex-shrink: 0;
  width: 28px;
}

.att-bubble {
  max-width: 75%;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
}

.att-bubble--visitor {
  background: linear-gradient(135deg, var(--att-accent) 0%, var(--att-accent-secondary) 100%);
  color: var(--att-white);
  border-radius: var(--att-bubble-radius) 4px var(--att-bubble-radius) var(--att-bubble-radius);
}

.att-bubble--agent {
  background: var(--att-white);
  color: var(--att-text);
  border-radius: 4px var(--att-bubble-radius) var(--att-bubble-radius) var(--att-bubble-radius);
  border: 1px solid var(--att-border);
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.att-msg-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  padding: 0 4px;
}

.att-msg-time {
  font-size: 10px;
  color: var(--att-text-muted);
}

.att-msg-name {
  font-size: 11px;
  color: var(--att-text-muted);
  font-weight: 500;
}

/* Typing indicator */
.att-typing {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 0 0 4px;
}

.att-typing-dots {
  background: var(--att-white);
  border: 1px solid var(--att-border);
  border-radius: 4px var(--att-bubble-radius) var(--att-bubble-radius) var(--att-bubble-radius);
  padding: 12px 16px;
  display: flex;
  gap: 4px;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.att-typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--att-text-muted);
  animation: att-typing-bounce 1.2s ease infinite;
}

.att-typing-dot:nth-child(1) { animation-delay: 0s; }
.att-typing-dot:nth-child(2) { animation-delay: 0.2s; }
.att-typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes att-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-6px); opacity: 1; }
}

/* Error state */
.att-error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px;
  text-align: center;
  color: var(--att-text-muted);
  gap: 8px;
}

.att-error-state svg {
  opacity: 0.4;
}

.att-error-state p {
  margin: 0;
  font-size: 14px;
}

.att-retry-btn {
  margin-top: 8px;
  background: var(--att-accent);
  color: var(--att-white);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s ease;
}

.att-retry-btn:hover {
  opacity: 0.9;
}

/* ===== QUICK REPLIES ===== */
.att-quick-replies {
  padding: 8px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background: var(--att-bg);
  border-top: 1px solid var(--att-border);
  flex-shrink: 0;
}

.att-quick-reply-chip {
  background: var(--att-white);
  border: 1.5px solid var(--att-accent);
  color: var(--att-accent);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
  line-height: 1.4;
}

.att-quick-reply-chip:hover {
  background: var(--att-accent);
  color: var(--att-white);
}

/* ===== HANDOFF CARD ===== */
.att-handoff-card {
  margin: 4px 0;
  background: var(--att-white);
  border: 1px solid var(--att-border);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 85%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.att-handoff-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--att-text);
}

.att-handoff-card-msg {
  font-size: 13px;
  color: var(--att-text-muted);
  margin: 0;
}

.att-handoff-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  transition: background 0.15s ease;
}

.att-handoff-btn:hover {
  background: #1ebe5d;
}

/* ===== COMPOSER ===== */
.att-composer {
  padding: 10px 12px;
  background: var(--att-white);
  border-top: 1px solid var(--att-border);
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.att-composer-input {
  flex: 1;
  border: 1.5px solid var(--att-border);
  border-radius: 12px;
  padding: 9px 14px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  overflow-y: auto;
  color: var(--att-text);
  background: var(--att-bg);
  transition: border-color 0.15s ease;
  outline: none;
  line-height: 1.4;
}

.att-composer-input::placeholder {
  color: var(--att-text-muted);
}

.att-composer-input:focus {
  border-color: var(--att-accent);
  background: var(--att-white);
}

.att-composer-send {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: var(--att-accent);
  color: var(--att-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  font-family: inherit;
}

.att-composer-send:hover:not(:disabled) {
  opacity: 0.9;
  transform: scale(1.05);
}

.att-composer-send:active:not(:disabled) {
  transform: scale(0.95);
}

.att-composer-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.att-composer-send svg {
  width: 18px;
  height: 18px;
}

/* ===== FOOTER (powered by) ===== */
.att-footer {
  padding: 6px 12px 8px;
  text-align: center;
  background: var(--att-white);
  border-top: 1px solid var(--att-border);
  flex-shrink: 0;
}

.att-footer a {
  font-size: 11px;
  color: var(--att-text-muted);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease;
}

.att-footer a:hover {
  color: var(--att-text);
}

/* ===== MOBILE RESPONSIVE ===== */
@media (max-width: 520px) {
  .att-panel {
    width: calc(100vw - 24px);
    height: calc(100dvh - 80px);
    bottom: 72px;
    ${positionProp}: 12px;
    ${oppositeProp}: 12px;
    border-radius: 18px;
  }

  .att-trigger {
    bottom: 16px;
    ${positionProp}: 16px;
  }
}

/* Focus trap outline */
.att-panel :focus-visible {
  outline: 2px solid var(--att-accent);
  outline-offset: 2px;
}

/* ── Estados de entrega, avisos ── */
.att-notice {
  text-align: center;
  color: var(--att-text-muted);
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 12px;
  margin: 6px 12px;
  border-radius: 10px;
  background: rgba(127, 127, 127, 0.1);
}
.att-offline { background: rgba(245, 158, 11, 0.14); color: inherit; }
.att-msg-row--sending .att-bubble { opacity: 0.7; }
.att-msg-row--failed .att-bubble { outline: 1px solid #dc2626; }
.att-msg-status { font-size: 11px; color: var(--att-text-muted); }
.att-msg-status--failed { color: #dc2626; }
.att-msg-retry {
  appearance: none;
  border: 0;
  background: none;
  padding: 0;
  margin-left: 6px;
  font: inherit;
  font-size: 11px;
  color: var(--att-accent);
  text-decoration: underline;
  cursor: pointer;
}
`;
}
